import React, { useEffect, useState } from 'react';
import styles from '../../styles/components/sections/GeneralSection.module.css';
import { replaceImageUrl } from '../../utils/helpers/imageUtils';
import { sanitizeHtml } from '../../utils/helpers/sanitize';
import { useSmoothScroll } from '../../utils/hooks/useSmoothScroll';
import { OptimizedImage } from '../common';

/**‰
 * A reusable general content section component that displays multiple sections
 * Each section can have image on left or right with content on the opposite side
 *
 * @param {Object} props
 * @param {Array|Object} props.data - The content data for the sections (can be array or single object)
 * @param {String} props.className - Optional additional CSS class
 * @param {Number} props.globalIndex - Global index of this section across all sections on the page
 * @param {String} props.categorySlug - The category slug to determine if this is a legal page
 */

const GeneralSection = ({ data, className = '', globalIndex = 0, categorySlug }) => {
    const { scrollToBooking } = useSmoothScroll();
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [isClient, setIsClient] = useState(false);



    // Set client-side flag
    useEffect(() => {
        setIsClient(true);
    }, []);

    // Detect system dark mode preference
    useEffect(() => {
        if (!isClient) return;

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        setIsDarkMode(mediaQuery.matches);

        const handleChange = e => {
            setIsDarkMode(e.matches);
            updateTheme(e.matches ? 'dark' : 'light');
        };

        mediaQuery.addEventListener('change', handleChange);

        // Check for saved theme preference
        const savedTheme = localStorage.getItem('theme');
        if (savedTheme) {
            setIsDarkMode(savedTheme === 'dark');
            updateTheme(savedTheme);
        } else {
            // Set initial theme based on system preference
            updateTheme(mediaQuery.matches ? 'dark' : 'light');
        }

        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [isClient]);

    // Detect mobile device
    useEffect(() => {
        if (!isClient) return;

        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, [isClient]);

    // Update theme on document
    const updateTheme = theme => {
        document.documentElement.setAttribute('data-theme', theme);
        localStorage.setItem('theme', theme);
    };

    // Toggle theme manually
    const toggleTheme = () => {
        const newTheme = isDarkMode ? 'light' : 'dark';
        setIsDarkMode(!isDarkMode);
        updateTheme(newTheme);
    };

    if (!data) return null;

    // Helper function to create HTML from the rich text content
    const createMarkup = htmlContent => {
        // Check if this is a legal page that needs raw HTML (for iframe content)
        const isLegalPage = categorySlug === 'terms-and-conditions' || categorySlug === 'privacy-policy';
        
        if (isLegalPage) {
            // For legal pages, wrap content with comprehensive Gilroy font styling for all elements
            const styledContent = `
                <div style="font-family: 'Gilroy', 'Gilroy-Regular', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif; font-weight: 400; color: var(--text-primary, #091323); line-height: 1.6;">
                    <style>
                        .legal-content * {
                            font-family: 'Gilroy', 'Gilroy-Regular', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif !important;
                            word-wrap: break-word !important;
                            word-break: break-word !important;
                            overflow-wrap: break-word !important;
                            hyphens: auto !important;
                            max-width: 100% !important;
                            box-sizing: border-box !important;
                            color: var(--text-primary, #091323) !important;
                            background-color: transparent !important;
                        }
                        .legal-content p, .legal-content li, .legal-content div {
                            word-wrap: break-word !important;
                            word-break: break-word !important;
                            overflow-wrap: break-word !important;
                            white-space: normal !important;
                            max-width: 100% !important;
                            color: var(--text-primary, #091323) !important;
                            padding: 0 !important;
                            background-color: transparent !important;
                        }
                        .legal-content h1, .legal-content h2, .legal-content h3, .legal-content h4, .legal-content h5, .legal-content h6 {
                            color: var(--text-primary, #091323) !important;
                            background-color: transparent !important;
                            font-weight: 600 !important;
                        }
                        .legal-content ul, .legal-content ol {
                            color: var(--text-primary, #091323) !important;
                            background-color: transparent !important;
                        }
                        .legal-content strong, .legal-content b {
                            color: var(--text-primary, #091323) !important;
                            background-color: transparent !important;
                        }
                        .legal-content em, .legal-content i {
                            color: var(--text-primary, #091323) !important;
                            background-color: transparent !important;
                        }
                        /* Dark mode specific overrides */
                        [data-theme='dark'] .legal-content * {
                            color: var(--text-primary, #ffffff) !important;
                            background-color: transparent !important;
                        }
                        [data-theme='dark'] .legal-content p, 
                        [data-theme='dark'] .legal-content li, 
                        [data-theme='dark'] .legal-content div,
                        [data-theme='dark'] .legal-content h1, 
                        [data-theme='dark'] .legal-content h2, 
                        [data-theme='dark'] .legal-content h3, 
                        [data-theme='dark'] .legal-content h4, 
                        [data-theme='dark'] .legal-content h5, 
                        [data-theme='dark'] .legal-content h6,
                        [data-theme='dark'] .legal-content ul, 
                        [data-theme='dark'] .legal-content ol,
                        [data-theme='dark'] .legal-content strong, 
                        [data-theme='dark'] .legal-content b,
                        [data-theme='dark'] .legal-content em, 
                        [data-theme='dark'] .legal-content i {
                            color: var(--text-primary, #ffffff) !important;
                            background-color: transparent !important;
                        }

                        span {
                        font-weight: 400 !important;
                         }
                        /* Table styles for legal content */
                        .legal-content table {
                            width: 100% !important;
                            max-width: 100% !important;
                            border-collapse: collapse !important;
                            margin: 1rem 0 !important;
                            display: table !important;
                            border: 1px solid #e5e7eb !important;
                        }
                        .legal-content table tbody {
                            min-width: 91vw !important;
                        }
                        .legal-content table tr {
                            display: table-row !important;
                            visibility: visible !important;
                            border-bottom: 1px solid #e5e7eb !important;
                        }
                        .legal-content table tr:last-child {
                            border-bottom: none !important;
                        }
                        .legal-content table td,
                        .legal-content table th {
                            display: table-cell !important;
                            visibility: visible !important;
                            padding: 12px 16px !important;
                            border: 1px solid #e5e7eb !important;
                            text-align: left !important;
                            vertical-align: top !important;
                            background-color: transparent !important;
                        }
                        .legal-content table tr:first-child td,
                        .legal-content table tr:first-child th {
                            font-weight: 600 !important;
                            background-color: #f9fafb !important;
                        }
                        .legal-content table tr:nth-child(even) td {
                            background-color: #f9fafb !important;
                        }
                        .legal-content table tr:nth-child(odd) td {
                            background-color: #ffffff !important;
                        }
                        /* Dark mode table styles */
                        [data-theme='dark'] .legal-content table {
                            border-color: transparent !important;

                        }
                        [data-theme='dark'] .legal-content table tr {
                            border-bottom-color: var(--border-color, #374151) !important;
                        }
                        [data-theme='dark'] .legal-content table td,
                        [data-theme='dark'] .legal-content table th {
                            border-color: var(--border-color, #374151) !important;
                        }
                        [data-theme='dark'] .legal-content table tr:first-child td,
                        [data-theme='dark'] .legal-content table tr:first-child th {
                            background-color: var(--background-color, #1f2937) !important;
                            color: var(--text-primary, #ffffff) !important;
                        }
                        [data-theme='dark'] .legal-content table tr:nth-child(even) td,
                        [data-theme='dark'] .legal-content table tr:nth-child(odd) td {
                            background-color: var(--background-color, #1f2937) !important;
                            color: var(--text-primary, #ffffff) !important;
                        }
                    </style>
                    <div class="legal-content">
                        ${htmlContent}
                    </div>
                </div>
            `;
            return { __html: styledContent };
        } else {
            // For other pages, sanitize as usual for security
            const sanitizedContent = sanitizeHtml(htmlContent);
            return { __html: sanitizedContent };
        }
    };

    // Convert single object to array for consistent processing
    const sections = Array.isArray(data) ? data : [data];

    // Filter out hidden sections and sort by order
    const visibleSections = sections
        .filter(section => !section.isHidden)
        .sort((a, b) => {
            const orderA = a.order || a.Order || 999;
            const orderB = b.order || b.Order || 999;
            return orderA - orderB;
        });

    if (visibleSections.length === 0) return null;

    const handleBook = () => scrollToBooking(100)

    return (
        <div className={`${className} ${styles['general-section']}`} >
            {visibleSections.map((sectionData, index) => {

                const hasImage = sectionData?.image?.image?.url;
                const imagePosition = sectionData?.image?.position;
                const isImageOnLeft = imagePosition === 'left';
                const isImageOnRight = imagePosition === 'right';
                const hasNoImage = !hasImage;


                return (
                    <section key={sectionData.id || index} className={`py-6 ${styles['section-spacing']}`}>
                        <div className="tw-container">
                            {/* Image on Left */}
                            {isImageOnLeft && (
                                <div className="row align-items-center">
                                    <div className="col-md-6 py-2">
                                        <OptimizedImage
                                            src={replaceImageUrl(sectionData?.image?.image?.url)}
                                            alt={sectionData?.image?.image?.alternativeText || sectionData?.title}
                                            className={styles['img-settings']}
                                            priority={false}
                                            loading="lazy"
                                            width={600}
                                            height={400}
                                            sizes="(max-width: 768px) 100vw, 50vw"
                                            quality={80}
                                        />
                                    </div>
                                    <div className="col-md-6 py-2">
                                        <div>
                                            <p className={styles['cc-headline']}>{sectionData.headline}</p>
                                            {globalIndex === 0 ? (
                                                <h1 className={styles['cc-heading']}>{sectionData.title}</h1>
                                            ) : (
                                                <h2 className={styles['cc-heading']}>{sectionData.title}</h2>
                                            )}
                                            <div className={styles['cc-description']} dangerouslySetInnerHTML={createMarkup(sectionData.description)} />
                                            {sectionData.BookingButton && (
                                                <div>
                                                    {!sectionData.BookingButton.isHidden && (
                                                        <div className="col-sm-4 mb-3">
                                                            <button
                                                                className="btn-form-active"
                                                                onClick={handleBook}
                                                                style={{
                                                                    filter: isDarkMode ? 'brightness(1.1) contrast(1.1)' : 'none',
                                                                }}>
                                                                {sectionData.BookingButton.name}
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Image on Right */}
                            {isImageOnRight && (
                                <div className="row align-items-center px-0">
                                    <div className="col-md-6 !pr-0">
                                        <div>
                                            <p className={styles['cc-headline']}>{sectionData.headline}</p>
                                            {globalIndex === 0 ? (
                                                <h1 className={styles['cc-heading']}>{sectionData.title}</h1>
                                            ) : (
                                                <h2 className={styles['cc-heading']}>{sectionData.title}</h2>
                                            )}
                                            <div className={styles['cc-description']} dangerouslySetInnerHTML={createMarkup(sectionData.description)} />
                                            {sectionData.BookingButton && (
                                                <div>
                                                    {!sectionData.BookingButton.isHidden && sectionData.BookingButton.name && (
                                                        <div className="col-sm-4 mb-3">
                                                            <button
                                                                className="btn-form-active"
                                                                onClick={handleBook}
                                                                style={{
                                                                    filter: isDarkMode ? 'brightness(1.1) contrast(1.1)' : 'none',
                                                                }}>
                                                                {sectionData.BookingButton.name}
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <OptimizedImage
                                            src={replaceImageUrl(sectionData?.image?.image?.url)}
                                            alt={sectionData?.image?.image?.alternativeText || sectionData?.title}
                                            className={styles['img-settings']}
                                            priority={index === 0 && globalIndex === 0}
                                            width={600}
                                            height={400}
                                            sizes="(max-width: 768px) 100vw, 50vw"
                                            quality={80}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* No Image */}
                            {hasNoImage && (
                                <div className="row align-items-center">
                                    <div className="col-md-12">
                                        <div>
                                            <p className={styles['cc-headline']}>{sectionData.headline}</p>
                                            {globalIndex === 0 ? (
                                                <h1 className={styles['cc-heading']}>{sectionData.title}</h1>
                                            ) : (
                                                <h2 className={styles['cc-heading']}>{sectionData.title}</h2>
                                            )}
                                            <div className={`${styles['cc-description']} ${styles['no-image-content']}`} dangerouslySetInnerHTML={createMarkup(sectionData.description)} />
                                            {sectionData.BookingButton && (
                                                <div>
                                                    {!sectionData.BookingButton.isHidden && sectionData.BookingButton.name && (
                                                        <div className="col-sm-4">
                                                            <button
                                                                className="btn-form-active"
                                                                onClick={handleBook}
                                                                style={{
                                                                    filter: isDarkMode ? 'brightness(1.1) contrast(1.1)' : 'none',
                                                                }}>
                                                                {sectionData.BookingButton.name}
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>
                );
            })}
        </div>
    );
};

export default GeneralSection;
